/**
 * Terms of Service route
 *
 * Public route — accessible both pre-login and in-app.
 * Uses i18n keys from the `common.legal.*` namespace.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

function TermsPage() {
  const { t } = useTranslation("common");

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      {/* Back link */}
      <div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/">
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {t("legal.backToHome")}
          </Link>
        </Button>
      </div>

      {/* Header */}
      <header className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">{t("legal.termsTitle")}</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          {t("legal.lastUpdated", { date: "2026-02-21" })}
        </p>
      </header>

      {/* Introduction */}
      <p className="text-sm leading-relaxed text-muted-foreground">
        {t("legal.termsIntro")}
      </p>

      {/* Section 1: The Service */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.termsServiceTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.termsServiceText")}
        </p>
      </section>

      {/* Section 2: Eligibility */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.termsEligibilityTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.termsEligibilityText")}
        </p>
      </section>

      {/* Section 3: Your Account */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.termsAccountTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.termsAccountText")}
        </p>
      </section>

      {/* Section 4: Acceptable Use */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.termsAcceptableUseTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.termsAcceptableUseText")}
        </p>
      </section>

      {/* Section 5: Health Disclaimer */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.termsHealthDisclaimerTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.termsHealthDisclaimerText")}
        </p>
      </section>

      {/* Section 6: Intellectual Property */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.termsIPTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.termsIPText")}
        </p>
      </section>

      {/* Section 7: Availability & Changes */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.termsAvailabilityTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.termsAvailabilityText")}
        </p>
      </section>

      {/* Section 8: Limitation of Liability */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.termsLiabilityTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.termsLiabilityText")}
        </p>
      </section>

      {/* Section 9: Governing Law */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.termsGoverningLawTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.termsGoverningLawText")}
        </p>
      </section>

      {/* Section 10: Contact */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.termsContactTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.termsContactText")}
        </p>
      </section>

      {/* Footer link to Privacy Policy */}
      <footer className="border-t pt-6 pb-4">
        <p className="text-sm text-muted-foreground">
          <Link to="/privacy" className="font-medium text-primary underline-offset-4 hover:underline">
            {t("legal.privacyPolicy")}
          </Link>
        </p>
      </footer>
    </div>
  );
}

export const Route = createFileRoute("/terms")({
  component: TermsPage,
});
